import { Pagination } from "@mui/material";
import { CalendarIcon, Cross2Icon } from "@radix-ui/react-icons";
import { Check, EyeIcon, Loader2, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import toast from "react-hot-toast";
import { Loader } from "../../components/Widget/Loader";
import { Tabs } from "../../components/Widget/Tab";
import { UploadButton } from "../../components/Widget/UploadButton";
import { find, store } from "../../services/service";
import {
  EXPORT_EXCEL_VENTE_ATTENTE_SUPPRESSION,
  // Ajout du endpoint pour suppression par lot
  // POST: /commerciale/abonnement/valider-supprersession-vente-par-lot
  RECHERCHER_VENTE_ATTENTE_SUPPRIMER,
  RECHERCHER_VENTE_SUPPRESSION,
  RECHERCHER_VENTE_SUPPRESSION_REJETES,
  SUPPRESSION_VENTE_BATCH,
  SUPPRIMER_VENTE_PAR_LOT,
  VALIDER_SUPPRESSION,
} from "../../Utils/constant";
import { checkTagValidity } from "../../Utils/Utils";

export const VentesSupprimees = () => {
  const [ventes, setVentes] = useState({ content: [], totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [size] = useState(10);
  const [param, setParam] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [selectedIds, setSelectedIds] = useState([]);
  // Gestion de la sélection d'une vente
  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Sélectionner tout
  const handleSelectAll = () => {
    if (ventes?.data?.length === selectedIds?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(ventes?.data?.map((d) => d.id));
    }
  };

  // Suppression par lot avec loading
  const [batchLoading, setBatchLoading] = useState(null); // null | 'valider' | 'rejeter'
  const handleBatchDelete = async (state) => {
    if (!selectedIds.length) return;
    setBatchLoading(state ? "valider" : "rejeter");
    try {
      await store(SUPPRESSION_VENTE_BATCH, "application/json", {
        abonnementIds: selectedIds,
        confirmer: state,
      });
      toast.success(state ? "Suppressions validées" : "Suppressions rejetées");
      setSelectedIds([]);
      loadVentes(page);
    } catch (err) {
      toast.error(
        err?.response?.data?.donnee || "Erreur lors de la suppression en lot",
      );
    } finally {
      setBatchLoading(null);
    }
  };
  // Tabs config using project Tabs component
  const tabs = [
    {
      index: 0,
      key: "ATTENTE_SUPPRIMER",
      value: "En attente de suppression",
      tag: "VENTES_EN_ATTENTE_SUPPRESSION",
    },
    {
      index: 1,
      key: "VENTE_SUPPRIMER",
      value: "Ventes supprimées",
      tag: "VALIDER_SUPPRESSION_VENTE",
    },
    { index: 2, key: "REJETES", value: "Rejetées", tag: "VENTES_SUPPRIMEES" },
  ];

  const [active, setActive] = useState(tabs[0]); // default to Ventes à supprimer

  const filteredTabsData = tabs
    .filter((tab) => checkTagValidity(tab.tag))
    .map((tab, index) => ({ ...tab, index }));

  const endpointMap = {
    ATTENTE_SUPPRIMER: RECHERCHER_VENTE_ATTENTE_SUPPRIMER,
    VENTE_SUPPRIMER: RECHERCHER_VENTE_SUPPRESSION,
    REJETES: RECHERCHER_VENTE_SUPPRESSION_REJETES,
  };

  const [actionToConfirm, setActionToConfirm] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);

  const requestValidate = (id, valider) => {
    setActionToConfirm({ id, valider });
    document.getElementById("confirm_action").showModal();
  };

  const confirmAction = async () => {
    if (!actionToConfirm) return;
    try {
      const { id, valider } = actionToConfirm;
      const path = `${VALIDER_SUPPRESSION.replace(
        "{id}",
        id,
      )}?valider=${valider}`;
      await store(path, "application/json", {}).then((res) => {
        toast.success(
          `Suppression ${valider ? "validée" : "refusée"} avec succès`,
        );
        loadVentes(page);
        document.getElementById("confirm_action").close();
      });

      setActionToConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.donnee || "Erreur lors de l'opération");
    }
  };

  const cancelAction = () => {
    document.getElementById("confirm_action").close();
    setActionToConfirm(null);
  };

  const openDetails = (sale) => {
    setSelectedSale(sale);
    document.getElementById("details_sale").showModal();
  };

  const loadVentes = async (p = 1, debut, fin) => {
    setLoading(true);
    try {
      const q = `?dateDebut=${debut ?? ""}&dateFin=${
        fin ?? ""
      }&param=${param}&page=${p - 1}&size=${size}`;
      const endpoint =
        endpointMap[active?.key] || RECHERCHER_VENTE_ATTENTE_SUPPRIMER;
      const res = await find(`${endpoint}${q}`);
      const data = res?.data?.donnee;
      setVentes(data);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (filteredTabsData.length !== 0) {
      setActive(filteredTabsData[0]);
    }
  }, []);

  useEffect(() => {
    loadVentes(1);
  }, [active]);

  const onSearch = () => loadVentes(1);

  return (
    <div className="p-4">
      <div className="mb-6">
        <h2 className="font-mtn text-3xl font-bold mb-2">
          Gestion de suppression de ventes
        </h2>
        <p className="text-sm text-gray-500">
          Gestion des ventes en attente de suppression, validées ou rejetées.
        </p>
      </div>
      <div className="flex justify-between items-center gap-2 mb-4">
        <button
          className="btn btn-sm bg-main border-main flex items-center min-w-[220px] justify-center"
          disabled={exportLoading}
          onClick={async () => {
            setExportLoading(true);
            try {
              const res = await store(
                EXPORT_EXCEL_VENTE_ATTENTE_SUPPRESSION,
                null,
                null,
                { responseType: "blob" },
              );
              if (res?.data) {
                const url = window.URL.createObjectURL(res.data);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                  "download",
                  "ventes_attente_suppression.xlsx",
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
              }
            } catch (err) {
              toast.error("Erreur lors de l'export");
            } finally {
              setExportLoading(false);
            }
          }}
        >
          {exportLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="animate-spin h-4 w-4" />
              Export en cours...
            </span>
          ) : (
            "Exporter Excel ventes en attente"
          )}
        </button>
        <UploadButton
          title="Supprimer des ventes par lot"
          uploadUrl={SUPPRIMER_VENTE_PAR_LOT}
          fetchData={() => loadVentes(page)}
          model={null}
        />
      </div>
      <div className="mb-4">
        <Tabs
          tabsData={filteredTabsData}
          active={active}
          setActive={setActive}
        />
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <input
            value={param}
            onChange={(e) => {
              setParam(e.target.value);
              onSearch();
            }}
            type="text"
            placeholder="Rechercher un prestataire par nom..."
            className="input input-bordered px-3 pl-10 pr-10 w-full h-11 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
        </div>
        <div className="px-3 py-2 border border-gray-300 rounded-lg text-sm h-11 flex items-center gap-2 max-w-sm">
          <CalendarIcon className="w-5 h-5 text-gray-400" />
          <ReactDatePicker
            dateFormat="dd/MM/yyyy"
            selected={startDate}
            onChange={(update) => {
              if (update[1] != null) {
                loadVentes(
                  1,
                  new Date(update[0]).toLocaleDateString("es-CL"),
                  new Date(update[1]).toLocaleDateString("es-CL"),
                );
              }
              setDateRange(update);
            }}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            placeholderText="Sélectionner une période"
            className="w-56 h-7 cursor-pointer text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
          />
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Mobile / small screens: cards */}
          <div className="xl:hidden grid md:grid-cols-2 gap-3">
            {ventes?.data?.length ? (
              ventes.data.map((d) => (
                <div key={d?.id} className="p-4 border rounded bg-white">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="text-sm font-semibold">
                        N° {d?.abonnement?.eligibilite?.numeroTicket || d?.id}
                      </p>
                      <p className="text-xs text-gray-500">
                        {d?.abonnement?.client?.nomPrenoms || "-"}
                      </p>
                      <p className="text-xs text-gray-500">
                        Forfait: {d?.abonnement?.forfait?.designation || "-"}
                      </p>
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(
                        d?.date || d?.abonnement?.dateCreation || Date.now(),
                      ).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    {/* status badge */}
                    <div
                      className={`px-2 py-1 text-xs rounded-full ${
                        active?.key === "REJETES"
                          ? "bg-red-100 text-red-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {active?.key === "ATTENTE_SUPPRESSION"
                        ? "En attente (demande)"
                        : active?.key === "ATTENTE_SUPPRIMER"
                          ? "À supprimer"
                          : "Rejetée"}
                    </div>

                    {/* actions only for non-rejetes */}
                    {active?.key !== "REJETES" && (
                      <>
                        <button
                          onClick={() => requestValidate(d?.id, true)}
                          className="btn btn-success btn-xs flex items-center gap-1"
                        >
                          <Check size={14} /> Valider
                        </button>
                        <button
                          onClick={() => requestValidate(d?.id, false)}
                          className="btn btn-error btn-xs flex items-center gap-1"
                        >
                          <X size={14} /> Refuser
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => openDetails(d)}
                      className="btn btn-outline btn-xs"
                    >
                      Détails
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-500">
                Aucune vente supprimée trouvée
              </div>
            )}
          </div>
          {active.key === "ATTENTE_SUPPRIMER" && (
            <div className="flex items-center gap-2 mb-4">
              <button
                className="btn bg-red-500 text-white btn-sm flex items-center gap-2"
                disabled={
                  selectedIds?.length === 0 ||
                  batchLoading === "valider" ||
                  batchLoading === "rejeter"
                }
                onClick={() => handleBatchDelete(true)}
              >
                {batchLoading === "valider" && (
                  <Loader2 className="animate-spin h-4 w-4" />
                )}
                Valider la suppression ({selectedIds?.length})
              </button>
              <button
                className="btn bg-main border-main btn-sm flex items-center gap-2"
                disabled={
                  selectedIds?.length === 0 ||
                  batchLoading === "valider" ||
                  batchLoading === "rejeter"
                }
                onClick={() => handleBatchDelete(false)}
              >
                {batchLoading === "rejeter" && (
                  <Loader2 className="animate-spin h-4 w-4" />
                )}
                Rejeter la sélection ({selectedIds?.length})
              </button>
            </div>
          )}

          {/* Desktop / xl+: table */}
          <div className="hidden xl:block border rounded-lg overflow-auto">
            <table className="table table-zebra w-full">
              <thead className="bg-main text-black font-mtn">
                <tr>
                  <th className="text-xs px-3 py-3">
                    {ventes?.data?.length !== 0 && (
                      <input
                        className="checkbox checkbox-sm"
                        type="checkbox"
                        checked={
                          ventes?.data?.length > 0 &&
                          selectedIds?.length === ventes?.data?.length
                        }
                        onChange={handleSelectAll}
                      />
                    )}
                  </th>
                  <th className="text-xs px-3 py-3">Ticket</th>
                  <th className="text-xs px-3 py-3">Client</th>
                  <th className="text-xs px-3 py-3">Téléphone</th>
                  <th className="text-xs px-3 py-3">Commune</th>
                  <th className="text-xs px-3 py-3">Prestataire</th>
                  <th className="text-xs px-3 py-3">Statut</th>
                  <th className="text-xs px-3 py-3">Date demande</th>
                  <th className="text-xs px-3 py-3">Agent</th>
                  <th className="text-xs px-3 py-3">Motif</th>
                  <th className="text-xs px-3 py-3" style={{ minWidth: 180 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="text-xs">
                {ventes?.data?.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-8">
                      Aucune donnée
                    </td>
                  </tr>
                ) : (
                  ventes?.data?.map((d) => (
                    <tr key={d?.id}>
                      <td className="px-3 py-3">
                        <input
                          className="checkbox checkbox-sm "
                          type="checkbox"
                          checked={selectedIds.includes(d.id)}
                          onChange={() => handleSelect(d.id)}
                        />
                      </td>
                      <td className="px-3 py-3">
                        {d?.abonnement?.eligibilite?.numeroTicket ||
                          d?.numeroTicket ||
                          d?.id}
                      </td>
                      <td className="px-3 py-3 truncate">
                        {d?.abonnement?.client?.nomPrenoms ||
                          d?.client?.nomPrenoms ||
                          "-"}
                      </td>
                      <td className="px-3 py-3">
                        {d?.abonnement?.client?.numero ||
                          d?.client?.numero ||
                          d?.numeroMtn ||
                          "-"}
                      </td>
                      <td className="px-3 py-3">
                        {d?.commune || d?.domicile?.commune || "-"}
                      </td>

                      <td className="px-3 py-3 truncate">
                        {d?.servicePartner?.designation || "-"}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`text-xs inline-block truncate ${
                            d?.statusSuppression === "EN_ATTENTE"
                              ? "bg-amber-100 text-amber-700"
                              : d?.statusSuppression === "REJETE"
                                ? "bg-red-100 text-red-700"
                                : "bg-green-100 text-green-700"
                          } rounded-lg px-2 py-1`}
                        >
                          {d?.statusSuppression
                            .replace(/_/g, " ")
                            .toLowerCase() || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-3 truncate">
                        {d?.dateDemandeSuppression
                          ? new Date(d?.dateDemandeSuppression).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-3 py-3 truncate">
                        {d?.agent?.nomPrenoms || d?.agent?.nomPrenoms || "-"}
                      </td>
                      <td className="px-3 py-3">
                        {d?.motifSuppression
                          ? d?.motifSuppression.replace(/^"|"$/g, "")
                          : "-"}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {d?.statusSuppression === "EN_ATTENTE" && (
                            <>
                              <button
                                onClick={() => requestValidate(d?.id, true)}
                                className="rounded w-7 h-7  bg-red-100 text-red-500 flex items-center justify-center gap-1"
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={() => requestValidate(d?.id, false)}
                                className="rounded w-7 h-7 flex items-center justify-center gap-1 bg-main"
                              >
                                <X size={14} />
                              </button>
                            </>
                          )}

                          <button
                            onClick={() => openDetails(d)}
                            className="rounded w-7 h-7 bg-gray-300 flex items-center justify-center text-gray-700"
                          >
                            <EyeIcon size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <dialog id="confirm_action" className="modal rounded-none">
            <div className="modal-box w-10/12 max-w-md">
              <div className="modal-action">
                <h1 className="mr-auto text-xl font-bold font-mtn">
                  Confirmer l'action
                </h1>
                <form method="dialog">
                  <button className="btn btn-sm">
                    <Cross2Icon />
                  </button>
                </form>
              </div>
              <div className="py-4">
                <p className="text-gray-700">
                  Voulez-vous vraiment{" "}
                  {actionToConfirm?.valider ? "valider" : "refuser"} la
                  suppression ?
                </p>
              </div>
              <div className="modal-action">
                <button onClick={cancelAction} className="btn btn-sm">
                  Annuler
                </button>
                <button
                  onClick={confirmAction}
                  className="btn bg-main border-main btn-sm text-white"
                >
                  {actionToConfirm?.valider ? "Valider" : "Refuser"}
                </button>
              </div>
            </div>
          </dialog>

          <dialog id="details_sale" className="modal rounded-none">
            <div className="modal-box w-10/12 max-w-2xl">
              <div className="modal-action">
                <h1 className="mr-auto text-xl font-bold font-mtn">
                  Détails vente
                </h1>
                <form method="dialog">
                  <button className="btn btn-sm">
                    <Cross2Icon />
                  </button>
                </form>
              </div>
              <div className="bg-white w-full max-w-3xl flex flex-col">
                {/* Header */}

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  <div className="space-y-8">
                    {/* Client & Abonnement */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Client & Abonnement
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Nom complet
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.client?.nomPrenoms ||
                              selectedSale?.abonnement?.client?.nomPrenoms ||
                              "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Téléphone
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.client?.numero ||
                              selectedSale?.numeroMtn ||
                              "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            N° Ticket
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.eligibilite
                              ?.numeroTicket ||
                              selectedSale?.numeroTicket ||
                              "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Ancien numero FTTH
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.ancienNumeroFtth || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Forfait
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.forfait?.designation ||
                              selectedSale?.forfait?.designation ||
                              "-"}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-sm text-gray-600 mb-1">
                            Équipement
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.forfait?.equipement
                              ?.designation ||
                              selectedSale?.equipement?.designation ||
                              "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Localisation */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Localisation
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Commune
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.commune ||
                              selectedSale?.domicile?.commune ||
                              "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Quartier
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.eligibilite?.pbo?.quartier
                              ?.designation ||
                              selectedSale?.quartier ||
                              "-"}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-sm text-gray-600 mb-1">
                            Adresse
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.domicile?.adresse || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">PBO</div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.eligibilite?.pbo?.nom || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Statut & Dates */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Statut & Dates
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Statut suppression
                          </div>
                          <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                            {selectedSale?.statusSuppression.replace(
                              /_/g,
                              " ",
                            ) || "-"}
                          </span>
                        </div>

                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Date demande
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.dateDemandeSuppression
                              ? new Date(
                                  selectedSale.dateDemandeSuppression,
                                ).toLocaleDateString("fr-FR", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Date création
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.dateCreation
                              ? new Date(
                                  selectedSale.dateCreation,
                                ).toLocaleDateString("fr-FR", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Agent & Prestataire */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Agent & Prestataire
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Agent
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.agent?.nomPrenoms || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Email agent
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.agent?.email || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Prestataire
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.servicePartner?.designation || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Intiateur de la demande de suppression
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Agent
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.initiateurSuppression.agent
                              ?.nomPrenoms || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Email agent
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.initiateurSuppression?.agent
                              ?.email || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Numero de telephone agent
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.initiateurSuppression.agent
                              ?.numero || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {selectedSale?.approbateurSuppression && (
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">
                          Approbateur de la suppression
                        </h3>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                          <div>
                            <div className="text-sm text-gray-600 mb-1">
                              Approbateur
                            </div>
                            <div className="text-base font-semibold text-gray-900">
                              {selectedSale?.approbateurSuppression.agent
                                ?.nomPrenoms || "-"}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 mb-1">
                              Email
                            </div>
                            <div className="text-base font-semibold text-gray-900">
                              {selectedSale?.approbateurSuppression?.agent
                                ?.email || "-"}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 mb-1">
                              Numero de telephone
                            </div>
                            <div className="text-base font-semibold text-gray-900">
                              {selectedSale?.approbateurSuppression.agent
                                ?.numero || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Motif */}
                    {selectedSale?.motifSuppression && (
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">
                          Motif de Suppression
                        </h3>
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {selectedSale.motifSuppression.replace(
                              /^"|"$/g,
                              "",
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </dialog>

          <div className="my-4 flex justify-end">
            <Pagination
              count={ventes?.totalPages || 0}
              page={page}
              onChange={(e, v) => loadVentes(v)}
            />
          </div>
        </>
      )}
    </div>
  );
};
export default VentesSupprimees;
